/**
 * VendorProductsPanel — used by Flowers & Pooja / Return Gift / Muhurtham Pai
 * vendors to upload up to 10 products. Each product = name + price + 1 photo.
 * Photo source: direct file upload OR Meta (Instagram / Facebook) import.
 *
 * Backend:
 *   GET /api/me/products
 *   PUT /api/me/products
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Upload, InstagramLogo, FloppyDisk, Spinner, ImageSquare } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PRODUCTS = 10;

const blank = () => ({ id: null, name: '', price: '', photo_url: '' });

const VendorProductsPanel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileRefs = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/me/products`, { withCredentials: true });
      setProducts(data.products || []);
      setEligible(true);
    } catch (err) {
      if (err?.response?.status === 403) setEligible(false);
      else toast.error(err?.response?.data?.detail || 'Could not load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return null;
  if (!eligible) return null;

  const updateRow = (i, patch) =>
    setProducts((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRow = (i) =>
    setProducts((rows) => rows.filter((_, idx) => idx !== i));

  const addRow = () => {
    if (products.length >= MAX_PRODUCTS) {
      toast.warning(`Maximum ${MAX_PRODUCTS} products`);
      return;
    }
    setProducts((rows) => [...rows, blank()]);
  };

  const handleFileUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const url = data.url?.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
      updateRow(i, { photo_url: url });
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingIdx(null);
      if (fileRefs.current[i]) fileRefs.current[i].value = '';
    }
  };

  const importFromMeta = async (i) => {
    // Pull the most recent Instagram / Facebook image from this user's
    // connected Meta account. Reuses the existing `/api/meta/portfolio/import`
    // — the first imported image becomes this row's photo.
    setUploadingIdx(i);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/meta/portfolio/import`,
        { max_images: 1 },
        { withCredentials: true },
      );
      const item = (data.items || data.imported || [])[0];
      if (!item) {
        toast.error(data?.message || 'Connect Instagram/Facebook first (see card above)');
        return;
      }
      const url = item.url?.startsWith('http') ? item.url : `${BACKEND_URL}${item.url}`;
      updateRow(i, { photo_url: url });
      toast.success('Imported from social');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not import from social');
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleSave = async () => {
    const cleaned = products
      .map((p) => ({
        id: p.id || undefined,
        name: String(p.name || '').trim(),
        price: Number(p.price) || 0,
        photo_url: String(p.photo_url || '').trim(),
      }))
      .filter((p) => p.name && p.price > 0 && p.photo_url);

    if (cleaned.length === 0) {
      toast.error('Add at least one product (name, price, photo all required)');
      return;
    }
    if (cleaned.length !== products.filter((p) => p.name || p.price || p.photo_url).length) {
      toast.warning('Some incomplete rows skipped — fill name, price, and photo for each');
    }

    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/me/products`,
        { products: cleaned }, { withCredentials: true });
      setProducts(data.products || []);
      toast.success(`Saved ${data.products?.length || 0} product${data.products?.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="vendor-products-panel">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <ImageSquare size={20} className="text-brand-primary" weight="duotone" /> Your Products ({products.length}/{MAX_PRODUCTS})
        </CardTitle>
        <CardDescription>
          Each product = 1 photo + name + price. Up to {MAX_PRODUCTS} items.
          Customers see these as a menu on your shop card and can tap any item for a full-screen preview.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {products.length === 0 && (
          <p className="text-xs text-text-secondary text-center py-2">
            No products yet — tap <strong>Add product</strong> to begin.
          </p>
        )}

        {products.map((p, i) => (
          <div key={p.id || `new-${i}`}
            className="border border-[#E8E1D5] p-3 grid grid-cols-12 gap-3 items-start"
            data-testid={`product-row-${i}`}>
            {/* Photo column */}
            <div className="col-span-12 sm:col-span-3 flex flex-col items-center gap-1">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.name || `Product ${i + 1}`}
                  className="w-full aspect-square object-cover border border-[#E8E1D5]"
                  data-testid={`product-photo-${i}`} />
              ) : (
                <div className="w-full aspect-square bg-stone-100 border border-dashed border-[#E8E1D5] flex items-center justify-center">
                  <ImageSquare size={32} className="text-stone-300" />
                </div>
              )}
              <input type="file" accept="image/*"
                ref={(el) => { fileRefs.current[i] = el; }}
                onChange={(e) => handleFileUpload(i, e)}
                className="hidden" />
              <div className="flex gap-1 w-full">
                <Button type="button" variant="outline" size="sm"
                  disabled={uploadingIdx === i}
                  onClick={() => fileRefs.current[i]?.click()}
                  className="rounded-none border-[#E8E1D5] flex-1 text-[10px]"
                  data-testid={`product-upload-${i}`}>
                  {uploadingIdx === i ? <Spinner size={11} className="animate-spin" /> : <Upload size={11} />}
                </Button>
                <Button type="button" variant="outline" size="sm"
                  disabled={uploadingIdx === i}
                  onClick={() => importFromMeta(i)}
                  className="rounded-none border-[#E8E1D5] flex-1 text-[10px]"
                  title="Import latest from connected Instagram / Facebook"
                  data-testid={`product-import-meta-${i}`}>
                  <InstagramLogo size={11} />
                </Button>
              </div>
            </div>
            {/* Name + price */}
            <div className="col-span-9 sm:col-span-8 space-y-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Name</Label>
                <Input value={p.name}
                  onChange={(e) => updateRow(i, { name: e.target.value })}
                  placeholder="Jasmine Garland (10 ft) / Silver Coin Gift / Pooja Thali"
                  className="rounded-none h-8 text-sm"
                  data-testid={`product-name-${i}`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Price (₹)</Label>
                <Input type="number" min={1}
                  value={p.price}
                  onChange={(e) => updateRow(i, { price: e.target.value })}
                  placeholder="330"
                  className="rounded-none h-8 text-sm w-32"
                  data-testid={`product-price-${i}`} />
              </div>
            </div>
            {/* Remove */}
            <div className="col-span-3 sm:col-span-1 flex justify-end">
              <Button type="button" variant="ghost" size="sm"
                onClick={() => removeRow(i)}
                className="text-rose-600 hover:bg-rose-50 rounded-none h-8"
                data-testid={`product-remove-${i}`}>
                <Trash size={14} />
              </Button>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-2 pt-1">
          <Button onClick={addRow}
            disabled={products.length >= MAX_PRODUCTS}
            variant="outline" size="sm"
            className="rounded-none border-[#E8E1D5]"
            data-testid="add-product-btn">
            <Plus size={14} className="mr-1.5" /> Add product
          </Button>
          <Button onClick={handleSave} disabled={saving}
            className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50"
            data-testid="save-products-btn">
            <FloppyDisk size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save catalog'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VendorProductsPanel;
